<template>
  <div class="form-builder">
    <!-- Header -->
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">Form Builder</h4>
          <p class="text-muted mb-0">Create and design custom forms</p>
        </div>
        <div>
          <b-button variant="outline-secondary" class="mr-1" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
            Back
          </b-button>
          <b-button variant="outline-primary" class="mr-1" @click="previewForm">
            <feather-icon icon="EyeIcon" size="16" class="mr-1" />
            Preview
          </b-button>
          <b-button variant="outline-success" class="mr-1" @click="saveForm">
            <feather-icon icon="SaveIcon" size="16" class="mr-1" />
            Save Draft
          </b-button>
          <b-button variant="success" @click="publishForm">
            <feather-icon icon="SendIcon" size="16" class="mr-1" />
            Publish Form
          </b-button>
        </div>
      </div>
    </b-card>

    <b-row>
      <!-- Form Settings Panel -->
      <b-col lg="3" md="4" sm="12" class="mb-3">
        <b-card title="Form Settings" class="mb-2">
          <b-form-group label="Form Title" label-for="form-title">
            <b-form-input
              id="form-title"
              v-model="formData.title"
              placeholder="Enter form title"
            />
          </b-form-group>
          
          <b-form-group label="Form Description" label-for="form-description">
            <b-form-textarea
              id="form-description"
              v-model="formData.description"
              placeholder="Enter form description"
              rows="3"
            />
          </b-form-group>

          <b-form-group label="Theme Color">
            <b-form-select v-model="formData.theme" :options="themeOptions" />
          </b-form-group>

          <!-- Background Selection -->
          <b-form-group label="Form Background">
            <b-form-radio-group v-model="formData.backgroundType" :options="[{ value: '', text: 'None' }, { value: 'color', text: 'Color' }, { value: 'image', text: 'Image' }]" />
            <div v-if="formData.backgroundType === 'color'" class="mt-1">
              <input type="color" v-model="formData.backgroundColor" style="width: 40px; height: 32px; border: none; background: none;" />
              <span class="ml-1">{{ formData.backgroundColor }}</span>
            </div>
            <div v-if="formData.backgroundType === 'image'" class="mt-1">
              <input type="file" accept="image/*" @change="onBackgroundImageChange" />
              <div v-if="formData.backgroundImageUrl" class="mt-1">
                <img :src="formData.backgroundImageUrl" alt="Background Preview" style="max-width: 100%; max-height: 80px; border-radius: 4px;" />
              </div>
            </div>
          </b-form-group>

          <!-- Logo Upload -->
          <b-form-group label="Company Logo">
            <input type="file" accept="image/*" @change="onLogoChange" />
            <div v-if="formData.logoUrl" class="mt-1 d-flex align-items-center">
              <img :src="formData.logoUrl" alt="Logo Preview" style="max-width: 120px; max-height: 60px; object-fit: contain; border-radius: 4px;" />
              <b-button size="sm" variant="outline-danger" class="ml-2" @click="formData.logoUrl = ''">Remove</b-button>
            </div>
          </b-form-group>

          <b-form-group label="Assign To">
            <b-form-select v-model="formData.assignedTo" :options="assignToOptions" />
          </b-form-group>

          <b-form-group v-if="formData.assignedTo === 'specific'" label="Select Users">
            <b-form-checkbox-group v-model="formData.specificUsers" :options="userOptions" />
          </b-form-group>

          <b-form-group label="Allow Anonymous">
            <b-form-checkbox v-model="formData.allowAnonymous" />
          </b-form-group>
        </b-card>

        <!-- Question Types Panel -->
        <b-card title="Question Types" class="mb-2">
          <div class="question-types">
            <div
              v-for="type in questionTypes"
              :key="type.id"
              class="question-type-item"
              @click="addQuestion(type.id)"
            >
              <div class="question-type-icon">
                <feather-icon :icon="type.icon" size="20" />
              </div>
              <div class="question-type-info">
                <h6 class="mb-0">{{ type.name }}</h6>
                <small class="text-muted">{{ type.description }}</small>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Form Builder Area -->
      <b-col lg="9" md="8" sm="12">
        <b-card title="Form Design" class="mb-2">
          <!-- Form Header -->
          <div class="form-header mb-4">
            <h3 class="text-center mb-2">{{ formData.title || 'Untitled Form' }}</h3>
            <p class="text-center text-muted mb-0">{{ formData.description || 'Form description' }}</p>
          </div>

          <!-- Questions List -->
          <div v-if="formData.questions.length === 0" class="empty-state text-center py-5">
            <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
            <h5 class="text-muted">No questions added yet</h5>
            <p class="text-muted">Click on a question type from the left panel to start building your form</p>
          </div>

          <draggable
            v-model="formData.questions"
            group="questions"
            class="questions-container"
            @start="drag = true"
            @end="drag = false"
          >
            <div
              v-for="(question, index) in formData.questions"
              :key="question.id"
              class="question-item mb-3"
            >
              <b-card :class="['question-card', { 'is-dragging': drag }]">
                <div v-if="question.type !== 'header'" class="question-header d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center">
                    <div class="question-number mr-2">
                      <b-badge variant="primary">{{ getQuestionNumber(index) }}</b-badge>
                    </div>
                    <div class="question-type-badge mr-2">
                      <b-badge :variant="getQuestionTypeVariant(question.type)">
                        {{ getQuestionTypeName(question.type) }}
                      </b-badge>
                    </div>
                  </div>
                  <div class="question-actions">
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      class="mr-1"
                      @click="duplicateQuestion(index)"
                    >
                      <feather-icon icon="CopyIcon" size="14" />
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="removeQuestion(index)"
                    >
                      <feather-icon icon="Trash2Icon" size="14" />
                    </b-button>
                  </div>
                </div>

                <!-- Question Content -->
                <div class="question-content">
                  <template v-if="question.type !== 'header'">
                    <b-form-group label="Question Text" label-for="question-text">
                      <b-form-input
                        v-model="question.text"
                        placeholder="Enter your question"
                        class="mb-2"
                      />
                    </b-form-group>
                  </template>
                  <template v-else>
                    <b-form-group label="Header Text">
                      <b-form-input
                        v-model="question.text"
                        placeholder="Enter header text"
                        class="mb-2"
                      />
                    </b-form-group>
                    <b-form-group label="Header Style">
                      <b-form-select v-model="question.headerStyle" :options="headerStyleOptions" />
                    </b-form-group>
                    <b-form-group label="Header Color">
                      <b-form-select v-model="question.headerColor" :options="headerColorOptions" />
                    </b-form-group>
                  </template>

                  <!-- Question Type Specific Fields -->
                  <div class="question-type-fields" v-if="question.type !== 'header'">
                    <!-- MCQ Options -->
                    <div v-if="question.type === 'mcq'" class="mcq-options">
                      <b-form-group label="Options">
                        <div
                          v-for="(option, optionIndex) in question.options"
                          :key="optionIndex"
                          class="option-item d-flex align-items-center mb-2"
                        >
                          <b-form-input
                            v-model="question.options[optionIndex]"
                            placeholder="Option text"
                            class="mr-2"
                          />
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="removeOption(question, optionIndex)"
                            :disabled="question.options.length <= 2"
                          >
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </div>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="addOption(question)"
                        >
                          <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                          Add Option
                        </b-button>
                      </b-form-group>
                      
                      <!-- Conditional Text for MCQ -->
                      <b-form-group label="Show Conditional Text">
                        <b-form-checkbox v-model="question.showConditionalText" />
                      </b-form-group>
                      
                      <div v-if="question.showConditionalText" class="conditional-text-section">
                        <b-form-group label="Conditional Text">
                          <b-form-textarea
                            v-model="question.conditionalText"
                            placeholder="Enter text that will appear when an option is selected"
                            rows="3"
                          />
                        </b-form-group>
                        <b-form-group label="Show for Selected Option">
                          <b-form-select v-model="question.conditionalOption" :options="question.options.map((opt, i) => ({ value: i, text: opt }))" />
                        </b-form-group>
                      </div>
                    </div>

                    <!-- Radio Button Options -->
                    <div v-if="question.type === 'radio'" class="radio-options">
                      <b-form-group label="Options">
                        <div
                          v-for="(option, optionIndex) in question.options"
                          :key="optionIndex"
                          class="option-item d-flex align-items-center mb-2"
                        >
                          <b-form-input
                            v-model="question.options[optionIndex]"
                            placeholder="Option text"
                            class="mr-2"
                          />
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="removeOption(question, optionIndex)"
                            :disabled="question.options.length <= 2"
                          >
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </div>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="addOption(question)"
                        >
                          <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                          Add Option
                        </b-button>
                      </b-form-group>
                      <div class="mt-2" v-for="(option, optionIndex) in question.options" :key="'cond-' + optionIndex">
                        <b-form-checkbox v-model="question.conditionalTexts[optionIndex].show">
                          Show conditional text for this option
                        </b-form-checkbox>
                        <b-form-textarea v-if="question.conditionalTexts[optionIndex].show" v-model="question.conditionalTexts[optionIndex].text" placeholder="Enter conditional text for this option" rows="2" class="mt-1" />
                      </div>
                    </div>

                    <!-- Checkbox Options -->
                    <div v-if="question.type === 'checkbox'" class="checkbox-options">
                      <b-form-group label="Options">
                        <div
                          v-for="(option, optionIndex) in question.options"
                          :key="optionIndex"
                          class="option-item d-flex align-items-center mb-2"
                        >
                          <b-form-input
                            v-model="question.options[optionIndex]"
                            placeholder="Option text"
                            class="mr-2"
                          />
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="removeOption(question, optionIndex)"
                            :disabled="question.options.length <= 2"
                          >
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </div>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          @click="addOption(question)"
                        >
                          <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                          Add Option
                        </b-button>
                      </b-form-group>
                      
                      <!-- Conditional Text for Checkbox -->
                      <b-form-group label="Show Conditional Text">
                        <b-form-checkbox v-model="question.showConditionalText" />
                      </b-form-group>
                      
                      <div v-if="question.showConditionalText" class="conditional-text-section">
                        <b-form-group label="Conditional Text">
                          <b-form-textarea
                            v-model="question.conditionalText"
                            placeholder="Enter text that will appear when options are selected"
                            rows="3"
                          />
                        </b-form-group>
                        <b-form-group label="Show for Selected Options">
                          <b-form-checkbox-group v-model="question.conditionalOptions">
                            <b-form-checkbox
                              v-for="(option, optionIndex) in question.options"
                              :key="optionIndex"
                              :value="optionIndex"
                            >
                              {{ option }}
                            </b-form-checkbox>
                          </b-form-checkbox-group>
                        </b-form-group>
                      </div>
                    </div>

                    <!-- One Liner -->
                    <div v-if="question.type === 'one-liner'" class="one-liner-field">
                      <b-form-group label="Placeholder Text">
                        <b-form-input
                          v-model="question.placeholder"
                          placeholder="Enter placeholder text"
                        />
                      </b-form-group>
                    </div>

                    <!-- Fill in the Blanks -->
                    <div v-if="question.type === 'fill-blanks'" class="fill-blanks-field">
                      <b-form-group label="Text with Blanks">
                        <b-form-textarea
                          v-model="question.textWithBlanks"
                          placeholder="Enter text with ___ for blanks (e.g., The capital of France is ___.)"
                          rows="3"
                        />
                      </b-form-group>
                    </div>

                    <!-- Brief Answer -->
                    <div v-if="question.type === 'brief'" class="brief-answer-field">
                      <b-form-group label="Expected Word Count">
                        <b-form-input
                          v-model="question.wordCount"
                          type="number"
                          placeholder="e.g., 100"
                        />
                      </b-form-group>
                    </div>
                  </div>

                  <!-- Question Settings -->
                  <div class="question-settings mt-3" v-if="question.type !== 'header'">
                    <b-row>
                      <b-col md="6">
                        <b-form-group label="Required">
                          <b-form-checkbox v-model="question.required" />
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </b-card>
            </div>
            <!-- Add Question Type Suggestions after the last question -->
            <div v-if="formData.questions.length > 0" class="add-question-suggestions text-center my-3">
              <div class="mb-2 font-weight-bold">Add Question Type Suggestions</div>
              <b-button-group>
                <b-button
                  v-for="type in questionTypes"
                  :key="type.id"
                  variant="outline-primary"
                  size="sm"
                  class="mx-1"
                  @click="addQuestion(type.id)"
                >
                  <feather-icon :icon="type.icon" size="16" class="mr-1" />
                  {{ type.name }}
                </b-button>
              </b-button-group>
            </div>
          </draggable>
        </b-card>
      </b-col>
    </b-row>

    <!-- Preview Modal -->
    <b-modal
      v-model="showPreview"
      title="Form Preview"
      size="lg"
      scrollable
      ok-title="Close"
      ok-only
    >
      <div class="form-preview">
        <div class="form-header mb-4">
          <h3 class="text-center mb-2">{{ formData.title || 'Untitled Form' }}</h3>
          <p class="text-center text-muted mb-0">{{ formData.description || 'Form description' }}</p>
        </div>

        <div
          v-for="block in previewBlocks"
          :key="block.type === 'one-liner-pair' ? 'pair-' + block.index : block.question.id"
          class="preview-question mb-4"
        >
          <!-- One Liner Pair Preview -->
          <div v-if="block.type === 'one-liner-pair'" class="one-liner-pair-preview">
            <b-row>
              <b-col md="6">
                <label :for="`input-${block.questions[0].id}`" class="font-weight-bold mb-1">
                  {{ block.index + 1 }}. {{ block.questions[0].text }}
                  <span v-if="block.questions[0].required" class="text-danger">*</span>
                </label>
                <b-form-input
                  :id="`input-${block.questions[0].id}`"
                  :placeholder="block.questions[0].placeholder || 'Enter your answer'"
                />
              </b-col>
              <b-col md="6">
                <label :for="`input-${block.questions[1].id}`" class="font-weight-bold mb-1">
                  {{ block.index + 2 }}. {{ block.questions[1].text }}
                  <span v-if="block.questions[1].required" class="text-danger">*</span>
                </label>
                <b-form-input
                  :id="`input-${block.questions[1].id}`"
                  :placeholder="block.questions[1].placeholder || 'Enter your answer'"
                />
              </b-col>
            </b-row>
          </div>

          <!-- One Liner Single Preview -->
          <div v-else-if="block.type === 'one-liner-single'" class="one-liner-preview">
            <h6 class="mb-2">
              {{ block.index + 1 }}. {{ block.question.text }}
              <span v-if="block.question.required" class="text-danger">*</span>
            </h6>
            <b-form-input
              :placeholder="block.question.placeholder || 'Enter your answer'"
            />
          </div>

          <!-- Header Preview -->
          <div v-else-if="block.question.type === 'header'" class="header-preview">
            <component 
              :is="block.question.headerStyle || 'h2'"
              :class="`text-${block.question.headerColor || 'primary'} mb-2`"
            >
              {{ block.question.text || 'Header Text' }}
            </component>
          </div>

          <!-- Other Question Types -->
          <template v-else>
            <h6 class="mb-2">
              {{ block.index + 1 }}. {{ block.question.text }}
              <span v-if="block.question.required" class="text-danger">*</span>
            </h6>
            <div v-if="block.question.type === 'mcq'" class="mcq-preview">
              <b-form-select
                v-model="block.question.selectedValue"
                :options="block.question.options.map((opt, i) => ({ value: i, text: opt }))"
                placeholder="Select an option"
                @change="updateConditionalText(block.question)"
              />
              <div v-if="block.question.showConditionalText && block.question.selectedValue === block.question.conditionalOption" class="conditional-text-display mt-2">
                <div class="alert alert-info">
                  {{ block.question.conditionalText }}
                </div>
              </div>
            </div>
            <div v-else-if="block.question.type === 'radio'" class="radio-preview">
              <b-form-radio-group v-model="block.question.selectedValue" @change="updateConditionalText(block.question)">
                <b-form-radio
                  v-for="(option, optionIndex) in block.question.options"
                  :key="optionIndex"
                  :value="optionIndex"
                >
                  {{ option }}
                </b-form-radio>
              </b-form-radio-group>
              <div v-if="block.question.showConditionalText && block.question.selectedValue === block.question.conditionalOption" class="conditional-text-display mt-2">
                <div class="alert alert-info">
                  {{ block.question.conditionalText }}
                </div>
              </div>
            </div>
            <div v-else-if="block.question.type === 'checkbox'" class="checkbox-preview">
              <b-form-checkbox-group v-model="block.question.selectedValues" @change="updateConditionalText(block.question)">
                <b-form-checkbox
                  v-for="(option, optionIndex) in block.question.options"
                  :key="optionIndex"
                  :value="optionIndex"
                >
                  {{ option }}
                </b-form-checkbox>
              </b-form-checkbox-group>
              <div v-if="block.question.showConditionalText && block.question.selectedValues && block.question.selectedValues.some(val => block.question.conditionalOptions.includes(val))" class="conditional-text-display mt-2">
                <div class="alert alert-info">
                  {{ block.question.conditionalText }}
                </div>
              </div>
            </div>
            <div v-else-if="block.question.type === 'fill-blanks'" class="fill-blanks-preview">
              <div v-html="formatFillBlanksText(block.question.textWithBlanks)"></div>
            </div>
            <div v-else-if="block.question.type === 'brief'" class="brief-answer-preview">
              <b-form-textarea
                placeholder="Enter your answer"
                rows="4"
              />
              <small class="text-muted">
                Expected word count: {{ block.question.wordCount || 'No limit' }}
              </small>
            </div>
          </template>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormRadioGroup,
  BFormRadio,
  BRow,
  BCol,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import draggable from 'vuedraggable'
import FormsService from '@/services/forms-service'

export default {
  name: 'CreateForm',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormRadioGroup,
    BFormRadio,
    BRow,
    BCol,
    BBadge,
    BModal,
    FeatherIcon,
    draggable,
  },
  data() {
    return {
      drag: false,
      showPreview: false,
      isEditMode: false,
      formData: {
        title: '',
        description: '',
        theme: 'primary',
        allowAnonymous: false,
        status: 'draft',
        assignedTo: 'all',
        specificUsers: [],
        questions: [],
        headerFields: {
          requestedBy: '',
          dateOfRequest: '',
          jobRequestTitle: '',
          department: '',
          location: '',
          requisitionNumber: ''
        },
        backgroundType: '',
        backgroundColor: '#ffffff',
        backgroundImageUrl: '',
        logoUrl: '',
      },
      questionTypes: [
        {
          id: 'header',
          name: 'Header',
          description: 'Section header or title',
          icon: 'HeadingIcon',
        },
        {
          id: 'mcq',
          name: 'Multiple Choice',
          description: 'Single selection from multiple options',
          icon: 'CheckSquareIcon',
        },
        {
          id: 'radio',
          name: 'Radio Button',
          description: 'Single selection from options',
          icon: 'CircleIcon',
        },
        {
          id: 'checkbox',
          name: 'Checkbox',
          description: 'Multiple selections allowed',
          icon: 'SquareIcon',
        },
        {
          id: 'one-liner',
          name: 'One Liner',
          description: 'Short text input',
          icon: 'TypeIcon',
        },
        {
          id: 'fill-blanks',
          name: 'Fill in the Blanks',
          description: 'Text with blank spaces',
          icon: 'Edit3Icon',
        },
        {
          id: 'brief',
          name: 'Brief Answer',
          description: 'Long text response',
          icon: 'FileTextIcon',
        },
      ],
      themeOptions: [
        { value: 'primary', text: 'Primary' },
        { value: 'success', text: 'Success' },
        { value: 'info', text: 'Info' },
        { value: 'warning', text: 'Warning' },
        { value: 'danger', text: 'Danger' },
        { value: 'dark', text: 'Dark' },
      ],
      assignToOptions: [
        { value: 'all', text: 'All Users' },
        { value: 'managers', text: 'Managers Only' },
        { value: 'employees', text: 'Employees Only' },
        { value: 'specific', text: 'Specific Users' },
      ],
      userOptions: [
        { value: 'user1', text: 'John Doe (Manager)' },
        { value: 'user2', text: 'Jane Smith (Employee)' },
        { value: 'user3', text: 'Mike Johnson (Manager)' },
        { value: 'user4', text: 'Sarah Wilson (Employee)' },
        { value: 'user5', text: 'David Brown (Manager)' },
      ],
      headerStyleOptions: [
        { value: 'h1', text: 'Heading 1 (Large)' },
        { value: 'h2', text: 'Heading 2 (Medium)' },
        { value: 'h3', text: 'Heading 3 (Small)' },
        { value: 'h4', text: 'Heading 4 (Extra Small)' },
      ],
      headerColorOptions: [
        { value: 'primary', text: 'Primary' },
        { value: 'secondary', text: 'Secondary' },
        { value: 'success', text: 'Success' },
        { value: 'info', text: 'Info' },
        { value: 'warning', text: 'Warning' },
        { value: 'danger', text: 'Danger' },
        { value: 'dark', text: 'Dark' },
        { value: 'light', text: 'Light' },
      ],
    }
  },
  computed: {
    previewBlocks() {
      const blocks = []
      const qs = this.formData.questions
      let i = 0
      while (i < qs.length) {
        if (qs[i].type === 'one-liner' && i + 1 < qs.length && qs[i + 1].type === 'one-liner') {
          // Pair of one-liners
          blocks.push({ type: 'one-liner-pair', questions: [qs[i], qs[i + 1]], index: i })
          i += 2
        } else if (qs[i].type === 'one-liner') {
          // Single one-liner
          blocks.push({ type: 'one-liner-single', question: qs[i], index: i })
          i += 1
        } else {
          // Other question types (including headers)
          blocks.push({ type: 'other', question: qs[i], index: i })
          i += 1
        }
      }
      return blocks
    }
  },
  methods: {
    addQuestion(type) {
      const question = {
        id: Date.now() + Math.random(),
        type,
        text: '',
        required: false,
        order: this.formData.questions.length + 1,
        options: type === 'mcq' || type === 'radio' || type === 'checkbox' ? ['Option 1', 'Option 2'] : [],
        placeholder: '',
        textWithBlanks: '',
        wordCount: '',
        headerStyle: type === 'header' ? 'h2' : '',
        headerColor: type === 'header' ? 'primary' : '',
        showDivider: type === 'header' ? true : false,
        showConditionalText: false,
        conditionalText: '',
        conditionalOption: null,
        conditionalOptions: [],
        selectedValue: null,
        selectedValues: [],
        conditionalTexts: type === 'radio' ? [{ show: false, text: '' }, { show: false, text: '' }] : [],
      }
      this.formData.questions.push(question)
    },
    removeQuestion(index) {
      this.formData.questions.splice(index, 1)
      this.updateQuestionOrder()
    },
    duplicateQuestion(index) {
      const question = JSON.parse(JSON.stringify(this.formData.questions[index]))
      question.id = Date.now() + Math.random()
      question.text = `${question.text} (Copy)`
      this.formData.questions.splice(index + 1, 0, question)
      this.updateQuestionOrder()
    },
    addOption(question) {
      question.options.push(`Option ${question.options.length + 1}`)
      if (question.type === 'radio') question.conditionalTexts.push({ show: false, text: '' })
    },
    removeOption(question, optionIndex) {
      if (question.options.length > 2) {
        question.options.splice(optionIndex, 1)
        if (question.type === 'radio') question.conditionalTexts.splice(optionIndex, 1)
      }
    },
    updateQuestionOrder() {
      this.formData.questions.forEach((question, index) => {
        question.order = index + 1
      })
    },
    getQuestionTypeName(type) {
      const questionType = this.questionTypes.find(qt => qt.id === type)
      return questionType ? questionType.name : type
    },
    getQuestionTypeVariant(type) {
      const variants = {
        header: 'light',
        mcq: 'primary',
        radio: 'info',
        checkbox: 'warning',
        'one-liner': 'success',
        'fill-blanks': 'secondary',
        brief: 'dark',
      }
      return variants[type] || 'primary'
    },
    formatFillBlanksText(text) {
      if (!text) return ''
      return text.replace(/___/g, '<input type="text" class="form-control d-inline-block" style="width: 150px;" placeholder="Answer">')
    },
    updateConditionalText(question) {
      // This method is used to trigger reactivity for conditional text display
      // The actual logic is handled in the template with computed properties
    },
    previewForm() {
      this.showPreview = true
    },
    async saveForm() {
      // Validate form
      if (!this.formData.title.trim()) {
        this.$bvToast.toast('Please enter a form title', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      if (this.formData.questions.length === 0) {
        this.$bvToast.toast('Please add at least one question to the form', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        if (this.isEditMode) {
          // Update existing form
          const response = await FormsService.updateForm(this.formData.id, this.formData)
          if (response.success && response.data) {
            this.formData = response.data
          }
        } else {
          // Create new form
          const response = await FormsService.createForm(this.formData)
          if (response.success && response.data) {
            this.formData = response.data
          }
        }

        // Show success message
        this.$bvToast.toast(
          this.isEditMode ? 'Form updated successfully!' : 'Form created successfully!',
          {
            title: 'Success',
            variant: 'success',
            solid: true,
          }
        )

        // Navigate back to forms list
        // this.$router.push({ name: 'tools-forms-list' })
      } catch (error) {
        this.$bvToast.toast('Error saving form', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async publishForm() {
      // Validate form
      if (!this.formData.title.trim()) {
        this.$bvToast.toast('Please enter a form title', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      if (this.formData.questions.length === 0) {
        this.$bvToast.toast('Please add at least one question to the form', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        // Set status to published
        this.formData.status = 'published'

        if (this.isEditMode) {
          // Update existing form
          const response = await FormsService.updateForm(this.formData.id, this.formData)
          if (response.success && response.data) {
            this.formData = response.data
          }
        } else {
          // Create new form
          const response = await FormsService.createForm(this.formData)
          if (response.success && response.data) {
            this.formData = response.data
          }
        }

        // Show success message
        this.$bvToast.toast('Form published successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })

        // Navigate back to forms list
        // this.$router.push({ name: 'tools-forms-list' })
      } catch (error) {
        this.$bvToast.toast('Error publishing form', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async loadFormData() {
      if (this.$route.params.id) {
        this.isEditMode = true
        try {
          const response = await FormsService.getFormById(this.$route.params.id)
          if (response.success && response.data) {
            this.formData = { ...response.data.form }
            console.log('formData', this.formData)
          } else {
            throw new Error('Form not found')
          }
        } catch (error) {
          this.$bvToast.toast('Form not found', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
          this.$router.push({ name: 'tools-forms-list' })
        }
      }
    },
    goBack() {
      this.$router.push({ name: 'tools-forms-list' })
    },
    onBackgroundImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          this.formData.backgroundImageUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    onLogoChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          this.formData.logoUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    getQuestionNumber(idx) {
      // Count only non-header questions up to idx
      return this.formData.questions.slice(0, idx + 1).filter(q => q.type !== 'header').length;
    },
  },
  async mounted() {
    await this.loadFormData()
  }
}
</script>

<style lang="scss" scoped>
.form-builder {
  .question-types {
    .question-type-item {
      display: flex;
      align-items: center;
      padding: 12px;
      border: 1px solid #e0e6ed;
      border-radius: 8px;
      margin-bottom: 8px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background-color: #f8f9fa;
        border-color: #7367f0;
        transform: translateY(-1px);
      }

      .question-type-icon {
        margin-right: 12px;
        color: #7367f0;
      }

      .question-type-info {
        flex: 1;

        h6 {
          margin-bottom: 2px;
          font-weight: 600;
        }

        small {
          font-size: 12px;
        }
      }
    }
  }

  .questions-container {
    min-height: 200px;
  }

  .question-item {
    .question-card {
      border: 1px solid #e0e6ed;
      transition: all 0.3s ease;

      &:hover {
        border-color: #7367f0;
        box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
      }

      &.is-dragging {
        opacity: 0.5;
        transform: rotate(5deg);
      }
    }

    .question-header {
      .question-number {
        .badge {
          font-size: 12px;
          padding: 6px 10px;
        }
      }

      .question-type-badge {
        .badge {
          font-size: 10px;
          padding: 4px 8px;
        }
      }
    }

    .option-item {
      .form-control {
        flex: 1;
      }
    }
  }

  .empty-state {
    color: #6e6b7b;
  }

  .form-preview {
    .preview-question {
      padding: 16px;
      border: 1px solid #e0e6ed;
      border-radius: 8px;
      background-color: #f8f9fa;

      h6 {
        color: #5e5873;
        font-weight: 600;
      }
    }

    .one-liner-pair-preview {
      .row {
        margin-left: 0;
        margin-right: 0;
      }

      .col-md-6 {
        padding-left: 8px;
        padding-right: 8px;
      }

      .form-group {
        margin-bottom: 0;
      }

      h6 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
      }
    }
  }
}

// Custom styles for fill in the blanks
.fill-blanks-preview {
  input[type="text"] {
    border: 1px dashed #7367f0;
    background-color: #f8f9fa;
    margin: 0 4px;
  }
}

// Custom styles for header questions
.header-preview {
  text-align: center;
  padding: 20px 0;
  
  h1, h2, h3, h4 {
    margin-bottom: 10px;
    font-weight: 600;
  }
  
  hr {
    margin: 15px auto;
    width: 50%;
    opacity: 0.7;
  }
}

.question-item {
  .header-field {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    border-left: 4px solid #7367f0;
  }
  
  .conditional-text-section {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    border-left: 4px solid #17a2b8;
    margin-top: 15px;
  }
}

.conditional-text-display {
  .alert {
    margin-bottom: 0;
    font-size: 14px;
  }
}

.add-question-suggestions {
  background: #f8f9fa;
  border: 1px dashed #7367f0;
  border-radius: 8px;
  padding: 18px 0 10px 0;
  margin-top: 12px;
  .btn-group .btn {
    margin: 0 4px 4px 0;
  }
}
</style>

<style scoped>
:root {
  --form-bg: #fff;
  --form-text: #222;
  --form-border: #ebe9f1;
}
@media (prefers-color-scheme: dark) {
  :root {
    --form-bg: #181824;
    --form-text: #f1f1f1;
    --form-border: #33344a;
  }
}
.form-builder, .b-card, .b-form-group, .question-type-item, .question-card {
  background: var(--form-bg) !important;
  color: var(--form-text) !important;
  border-color: var(--form-border) !important;
}
.b-form-input, .b-form-select, .b-form-textarea {
  font-size: 0.98rem;
  padding: 6px 10px;
}
.b-form-group, .option-item, .question-type-item, .question-card {
  margin-bottom: 8px !important;
}
.b-card {
  padding: 12px 14px !important;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .form-builder {
    padding: 0.5rem;
  }
  
  .card {
    margin-bottom: 1rem;
  }
  
  .card-header {
    padding: 0.75rem;
  }
  
  .card-body {
    padding: 0.75rem;
  }
  
  .btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
    margin-bottom: 0.25rem;
  }
  
  .btn-group .btn {
    margin-right: 0.25rem;
  }
  
  .form-label {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .form-control {
    font-size: 0.9rem;
  }
  
  .question-type-item {
    padding: 0.75rem;
  }
  
  .question-type-info h6 {
    font-size: 0.9rem;
  }
  
  .question-type-info small {
    font-size: 0.75rem;
  }
  
  .question-header {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .question-actions {
    margin-top: 0.5rem;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  
  .question-actions .btn {
    margin-left: 0.25rem;
  }
  
  .option-item {
    flex-direction: column;
  }
  
  .option-item .form-control {
    margin-top: 0.5rem;
  }
  
  .option-item .btn {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}

@media (max-width: 576px) {
  .form-builder {
    padding: 0.25rem;
  }
  
  .card-header {
    padding: 0.5rem;
  }
  
  .card-body {
    padding: 0.5rem;
  }
  
  .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
  
  .form-label {
    font-size: 0.8rem;
  }
  
  .form-control {
    font-size: 0.8rem;
  }
  
  .question-type-item {
    padding: 0.5rem;
  }
  
  .question-type-info h6 {
    font-size: 0.8rem;
  }
  
  .question-type-info small {
    font-size: 0.7rem;
  }
  
  .question-header {
    flex-direction: column;
  }
  
  .question-actions {
    margin-top: 0.5rem;
    width: 100%;
  }
  
  .question-actions .btn {
    flex: 1;
    margin-left: 0.25rem;
  }
  
  .option-item {
    flex-direction: column;
  }
  
  .option-item .form-control {
    margin-top: 0.5rem;
  }
  
  .option-item .btn {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .d-flex.justify-content-between > div:last-child {
    margin-top: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between > div:last-child .btn {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
  }
}
</style>
